/**
 * The questions purchasers asked about an offering, waiting for an answer (17 CFR 230.502(b)(2)(v);
 * S-30). The Account Executive sees their own clients' questions; a manager sees all of them. The
 * answer goes to the purchaser and is kept with the question.
 */
import { useCallback, useEffect, useState } from 'react';
import regDStaffService, { type OpenQuestion } from '../../services/regDStaff.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDateTime } from '../../lib/datetime';
import { colors } from '../../styles/theme';

const inputStyle = { border: `1px solid ${colors.border}` };

function QuestionRow({
  question,
  onDone,
  onError,
}: Readonly<{
  question: OpenQuestion;
  onDone: () => void;
  onError: (message: string) => void;
}>): JSX.Element {
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async (): Promise<void> => {
    setBusy(true);
    try {
      await regDStaffService.answerQuestion(question.question_id, answer.trim());
      onDone();
    } catch (e: unknown) {
      onError(getApiErrorMessage(e, 'Could not send the answer.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="rounded-xl p-4" style={{ border: `1px solid ${colors.border}` }}>
      <div className="text-xs mb-1" style={{ color: colors.muted }}>
        {`${question.client_name} · asked ${formatDateTime(question.asked_at)}`}
      </div>
      <p className="text-sm mb-3" style={{ color: colors.ink }}>
        {question.question}
      </p>
      <label htmlFor={`answer-${question.question_id}`} className="block text-xs font-semibold mb-1"
             style={{ color: colors.inkSoft }}>
        Your answer
      </label>
      <textarea
        id={`answer-${question.question_id}`}
        value={answer}
        rows={3}
        maxLength={4000}
        disabled={busy}
        onChange={(e) => setAnswer(e.target.value)}
        className="w-full px-3 py-2 rounded-lg text-sm mb-3"
        style={inputStyle}
      />
      <button
        type="button"
        disabled={busy || answer.trim().length < 2}
        onClick={() => { void send(); }}
        className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
        style={{ background: colors.navy, color: '#FFFFFF' }}
      >
        {busy ? 'Sending…' : 'Send the answer'}
      </button>
    </li>
  );
}

export default function OfferingQuestionsPanel(): JSX.Element {
  const [items, setItems] = useState<OpenQuestion[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setItems(await regDStaffService.listQuestions());
      setError(null);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load the questions.'));
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <section className="rounded-2xl p-5 mb-6"
             style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
      <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>
        Questions about offerings
      </h2>
      <p className="text-sm mb-4" style={{ color: colors.muted }}>
        A purchaser must have the chance to ask questions and receive answers before they buy. Each
        question and its answer is kept, with the date.
      </p>
      {error && (
        <div className="mb-4 p-3 rounded-lg text-sm" role="alert"
             style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}>
          {error}
        </div>
      )}
      {items === null && error === null && (
        <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>
      )}
      {items?.length === 0 && (
        <p className="text-sm" style={{ color: colors.muted }}>No question is waiting.</p>
      )}
      <ul className="space-y-3">
        {items?.map((q) => (
          <QuestionRow key={q.question_id} question={q} onDone={() => { void reload(); }} onError={setError} />
        ))}
      </ul>
    </section>
  );
}
